import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { TextField } from '@material-ui/core';
import { grey } from '@material-ui/core/colors';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Chip from '@material-ui/core/Chip';
import Grid from '@material-ui/core/Grid';
import InfoHover from '../../../controls/InfoHover';

const customDelimiter = '[_internal_]DELIMITER';

const styles = () => ({
    whiteBackground: {
        backgroundColor: 'white',
    },
    grey: {
        color: grey[600],
    },
    customText: {
        marginLeft: 12,
        marginRight: 12,
    },
});

const CustomAutocomplete = withStyles((theme) => ({
    tag: {
        backgroundColor: theme.palette.primary.main,
        height: 24,
        position: 'relative',
        zIndex: 0,
        '& .MuiChip-label': {
            color: theme.palette.getContrastText(theme.palette.primary.main),
        },
        '& .MuiChip-deleteIcon': {
            color: theme.palette.primary.main,
        },
        '&:after': {
            content: '""',
            right: 10,
            top: 6,
            height: 12,
            width: 12,
            position: 'absolute',
            backgroundColor: 'white',
            zIndex: -1,
        },
    },
}))(Autocomplete);

class RepeatableFieldDetails extends React.Component {
    state = {
        primary: [],
        secondary: [],
        fieldNames: [],
    };

    componentDidMount() {
        let fields = this.props.field.fields.map((fields) => fields.field);
        this.setState({ fieldNames: fields });
        this.splitExpressions();
    }

    splitExpressions = () => {
        const primaryAsArray = this.convertExpressionForDialog(this.props.field.displayExpression);
        const secondaryAsArray = this.convertExpressionForDialog(this.props.field.secondaryDisplayExpression);

        this.setState({
            primary: primaryAsArray,
            secondary: secondaryAsArray,
        });
    };

    convertExpressionForDialog = (displayExpression) => {
        if (displayExpression) {
            let fieldInBrackets = /\{(.*?)\}/g;
            let internalExpression = displayExpression;
            let match;

            while ((match = fieldInBrackets.exec(displayExpression))) {
                internalExpression = internalExpression.replace(
                    match[0],
                    `${customDelimiter}${match[1]}${customDelimiter}`,
                );
            }

            return internalExpression
                .split(customDelimiter)
                .filter((expression) => expression)
                .map((expression) => expression.trim());
        } else {
            return [];
        }
    };

    concatExpression = (target, listOfExpressions) => {
        let str = '';
        let fieldNames = this.state.fieldNames;
        listOfExpressions.forEach((expression, index) => {
            if (fieldNames.includes(expression)) {
                str += '{' + expression + '}';
            } else {
                str += expression;
            }

            if (index !== listOfExpressions.length - 1) {
                str += ' ';
            }
        });

        target.value = str;

        this.props.handleChange({ target });
    };

    setPrimary = (event, values) => {
        let target = {
            id: 'displayExpression',
            value: values,
        };
        this.setState({ primary: values }, () => this.concatExpression(target, this.state.primary));
    };

    setSecondary = (event, values) => {
        let target = {
            id: 'secondaryDisplayExpression',
            value: values,
        };
        this.setState({ secondary: values }, () => this.concatExpression(target, this.state.secondary));
    };

    render() {
        return (
            <React.Fragment>
                <Typography variant="h5">
                    Display
                    <InfoHover
                        text={
                            'These display settings define what a user will see after filling out the repeatable field and adding a record to the list. Primary and Secondary determine the order and font style of the display.'
                        }
                    />
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <CustomAutocomplete
                            multiple
                            freeSolo
                            id="displayExpression"
                            name="displayExpression"
                            options={this.state.fieldNames}
                            value={this.state.primary}
                            onChange={this.setPrimary}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    className={this.props.classes.whiteBackground}
                                    label="Primary"
                                    margin="normal"
                                />
                            )}
                            renderTags={(tagValues, getTagProps) => {
                                return tagValues.map((value, index) =>
                                    this.state.fieldNames.includes(value) ? (
                                        <Chip key={index.toString()} {...getTagProps({ index })} label={value} />
                                    ) : (
                                        <Typography key={index.toString()} className={this.props.classes.customText}>
                                            {value}
                                        </Typography>
                                    ),
                                );
                            }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <CustomAutocomplete
                            multiple
                            freeSolo
                            id="secondaryDisplayExpression"
                            name="secondaryDisplayExpression"
                            options={this.state.fieldNames}
                            value={this.state.secondary}
                            onChange={this.setSecondary}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    className={this.props.classes.whiteBackground}
                                    label="Secondary"
                                    margin="normal"
                                />
                            )}
                            renderTags={(tagValues, getTagProps) => {
                                return tagValues.map((value, index) =>
                                    this.state.fieldNames.includes(value) ? (
                                        <Chip key={index.toString()} {...getTagProps({ index })} label={value} />
                                    ) : (
                                        <Typography key={index.toString()} className={this.props.classes.customText}>
                                            {value}
                                        </Typography>
                                    ),
                                );
                            }}
                        />
                    </Grid>
                </Grid>
            </React.Fragment>
        );
    }
}

export default withStyles(styles)(RepeatableFieldDetails);
